/**
 * Rechargement après déploiement — deux situations, deux réactions.
 *
 * ─── LE PROBLÈME ──────────────────────────────────────────────────────────
 * Un onglet ouvert avant un déploiement garde en mémoire le manifeste de
 * l'ANCIEN build. Le prochain chunk demandé n'existe plus, Vercel renvoie la
 * page SPA en `text/html`, le navigateur refuse ce type MIME pour un module :
 *   « Failed to fetch dynamically imported module: …/_nuxt/DhrQYzRI.js »
 * et le visiteur tombe sur la page d'erreur. (Incident prod 2026-09-11.)
 *
 * ─── POURQUOI PAS `emitRouteChunkError` TOUT SEUL ─────────────────────────
 * Aucune des valeurs livrées par Nuxt ne convient, parce qu'elles traitent
 * deux situations très différentes de la même façon :
 *
 *  · `"automatic"` (défaut) ne recharge que si l'échec remonte par
 *    `router.onError`, c'est-à-dire UNIQUEMENT pendant une navigation. Un
 *    composant paresseux qui casse en dehors de ce circuit — le cas de
 *    l'incident — passe à travers et affiche la page d'erreur.
 *
 *  · `"automatic-immediate"` recharge bien dans les deux cas, mais il
 *    recharge AUSSI dès qu'un nouveau build est détecté, même quand la page
 *    marche parfaitement. Sur donoor.org, chaque push sur `main` déploie :
 *    un donateur en train de saisir son montant et son email verrait sa page
 *    se recharger sous ses doigts, et perdrait sa saisie (`persistState` ne
 *    sauve que le payload Nuxt, pas le contenu des champs). Le remède serait
 *    pire que le mal.
 *
 * ─── CE QU'ON FAIT À LA PLACE ─────────────────────────────────────────────
 * `emitRouteChunkError: "manual"` (nuxt.config) : Nuxt émet bien l'événement
 * `app:chunkError` mais n'installe AUCUN rechargement automatique. On décide
 * ici, selon que la page est déjà cassée ou non :
 *
 *  1. `app:chunkError` → la page est DÉJÀ cassée, un morceau de code n'a pas
 *     pu être chargé. On recharge tout de suite : il n'y a plus rien à perdre,
 *     et l'alternative est la page d'erreur plein écran.
 *
 *  2. `app:manifest:update` (un nouveau build est en ligne, détecté par le
 *     sondage de `builds/latest.json`) → la page MARCHE encore. On ne coupe
 *     personne : on arme un rechargement pour la PROCHAINE navigation, qui
 *     fait de toute façon repartir la page à zéro.
 *
 * ─── LE VRAI CORRECTIF, EN AMONT ──────────────────────────────────────────
 * Ce plugin est un filet. Le correctif de fond est la **Skew Protection**
 * Vercel (incluse dans le plan Pro déjà payé, à activer dans Settings ▸
 * Advanced) : les anciens chunks restent servis pour les onglets déjà
 * ouverts, donc il n'y a plus de chunk manquant du tout. Nuxt la gère sans
 * une ligne de code. Ce filet reste utile au-delà de la « Maximum Age » de la
 * Skew Protection (1 jour par défaut) : un onglet laissé ouvert plus
 * longtemps retombe sur le cas 1.
 */
import { joinURL } from "ufo";

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  const config = useRuntimeConfig();

  /**
   * Recharge l'application sur la MÊME URL. `persistState` réinjecte le
   * payload Nuxt pour que le rendu reparte du même état de données.
   * Nuxt pose son propre verrou anti-boucle : un rechargement par chemin
   * toutes les 10 s maximum.
   */
  // Seul `fullPath` sert : un type structurel évite de dépendre de la copie de
  // vue-router que Nuxt embarque (ses types diffèrent de ceux de l'app).
  const rechargerSur = (to: { fullPath: string }) => {
    reloadNuxtApp({
      path: joinURL(config.app.baseURL, to.fullPath),
      persistState: true,
    });
  };

  // Destination de la navigation EN COURS. Sans elle, un chunk qui casse
  // PENDANT une navigation ferait recharger la page qu'on quitte au lieu de
  // celle qu'on demande : le clic serait perdu et l'utilisateur resterait sur
  // place sans comprendre. (Même précaution que le plugin natif de Nuxt.)
  let navigationEnCours: { fullPath: string } | null = null;
  addRouteMiddleware((to) => {
    navigationEnCours = to;
  });

  // 1. Chunk introuvable : la page est déjà cassée → on recharge maintenant,
  //    sur la destination visée si une navigation était en cours.
  nuxtApp.hook("app:chunkError", () => {
    rechargerSur(navigationEnCours ?? router.currentRoute.value);
  });

  // 2. Nouveau build détecté alors que la page fonctionne : on attend la
  //    prochaine navigation pour ne rien interrompre.
  nuxtApp.hook("app:manifest:update", () => {
    router.beforeResolve(rechargerSur);
  });
});
